/**
 * The emoji the picker offers: the whole Unicode set, by its own groups.
 *
 * `unicode-emoji-json` is data only — no picker UI, no React — which is what we
 * want, since the picker here is already built to the editor's own chrome. It
 * is ~400KB of JSON, so it is fetched on FIRST OPEN rather than imported at the
 * top: someone who never inserts an emoji never downloads the catalogue.
 */

/** One emoji from each group, to label its tab. */
const GROUP_ICONS = {
    smileys_emotion: '🙂',
    people_body: '👍',
    animals_nature: '🌿',
    food_drink: '☕',
    travel_places: '✈️',
    activities: '⚽',
    objects: '📄',
    symbols: '✅',
    flags: '🏳️',
};

let cache = null;
let inFlight = null;

/**
 * The groups, each `{ id, icon, emoji: [{ char, name, keywords }] }`.
 *
 * `keywords` is the slug with its underscores opened out, so a search for
 * "thumbs up" reaches `thumbs_up` as readily as the name does.
 */
export function loadEmojiGroups() {
    if (cache) return Promise.resolve(cache);
    if (inFlight) return inFlight;

    inFlight = import('unicode-emoji-json/data-by-group.json')
        .then((mod) => {
            const raw = mod.default ?? mod;
            cache = raw.map((group) => ({
                id: group.slug,
                icon: GROUP_ICONS[group.slug] ?? group.emojis?.[0]?.emoji ?? '•',
                name: group.name,
                emoji: (group.emojis || []).map((e) => ({
                    char: e.emoji,
                    name: e.name,
                    keywords: `${e.name} ${String(e.slug || '').replace(/_/g, ' ')}`.toLowerCase(),
                })),
            }));
            return cache;
        })
        .catch(() => {
            inFlight = null;
            return [];
        });

    return inFlight;
}

/** Every emoji whose name or slug contains `query`, case-insensitively. */
export function searchEmoji(groups, query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (const group of groups) {
        for (const emoji of group.emoji) {
            if (emoji.keywords.includes(q)) out.push(emoji);
        }
    }
    return out;
}
